// Table Component - Handles trades table display and interactions
class TableComponent {
    constructor() {
        this.tableElement = null;
        this.bodyElement = null;
        this.trades = [];
        this.filteredTrades = [];
        this.sortColumn = 'file_timestamp';
        this.sortOrder = 'desc';
        this.currentPage = 1;
        this.tradesPerPage = 50;
        this.renderTimeout = null;
        this.onRowClick = null;
        this.isInitialized = false;
        
        // Don't auto-initialize - wait for DOM elements to exist
    }

    /**
     * Initialize table components
     */
    initializeTable() {
        this.tableElement = document.getElementById('trades-table');
        this.headerElement = document.getElementById('table-header');
        this.bodyElement = document.getElementById('table-body');
        
        if (!this.tableElement || !this.headerElement || !this.bodyElement) return;

        if (this.isInitialized) {
            // Clear existing content and reinitialize
            this.headerElement.innerHTML = '';
            this.bodyElement.innerHTML = '';
        }

        // Ensure explicit column widths using a shared colgroup
        this.applyColumnWidths();

        this.createTableHeader();
        this.setupEventListeners();
        this.isInitialized = true;
    }

    /**
     * Insert or update a shared colgroup to enforce explicit column widths
     */
    applyColumnWidths() {
        if (!this.tableElement) return;

        // Remove existing managed colgroup if present
        const existing = this.tableElement.querySelector('colgroup.trades-colgroup');
        if (existing && existing.parentElement === this.tableElement) {
            this.tableElement.removeChild(existing);
        }

        const colgroup = document.createElement('colgroup');
        colgroup.className = 'trades-colgroup';

        // 9-column width map: Asset, Direction, Entry, Target, Stop, Confidence, R/R, Time, Actions
        const widths = ['12%', '10%', '14%', '14%', '14%', '12%', '8%', '10%', '6%'];
        widths.forEach(w => {
            const col = document.createElement('col');
            col.style.width = w;
            colgroup.appendChild(col);
        });

        // Insert before thead to ensure widths apply to header and body
        if (this.tableElement.firstChild) {
            this.tableElement.insertBefore(colgroup, this.tableElement.firstChild);
        } else {
            this.tableElement.appendChild(colgroup);
        }
    }

    /**
     * Create table header with sortable columns
     */
    createTableHeader() {
        const columns = [
            { id: 'asset', label: 'Asset', sortable: true },
            { id: 'direction', label: 'Direction', sortable: true },
            { id: 'entry_price', label: 'Entry Price / Current Price', sortable: true },
            { id: 'target_price', label: 'Target', sortable: true },
            { id: 'stop_price', label: 'Stop', sortable: true },
            { id: 'confidence', label: 'Confidence', sortable: true },
            { id: 'risk_reward', label: 'Reward/Risk', sortable: true },
            { id: 'file_timestamp', label: 'Time', sortable: true },
            { id: 'actions', label: 'Actions', sortable: false }
        ];

        // Clear header safely
        while (this.headerElement.firstChild) {
            this.headerElement.removeChild(this.headerElement.firstChild);
        }
        const headerRow = document.createElement('tr');

        columns.forEach(column => {
            const th = document.createElement('th');
            th.textContent = column.label;
            th.dataset.column = column.id;
            
            if (column.sortable) {
                th.classList.add('sortable');
                th.addEventListener('click', () => this.handleSort(column.id));
            }
            
            headerRow.appendChild(th);
        });

        this.headerElement.appendChild(headerRow);
        this.updateSortIndicators();
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Export button
        const exportBtn = document.getElementById('export-btn');
        if (exportBtn) {
            exportBtn.addEventListener('click', () => this.exportTrades());
        }
    }

    /**
     * Update trades data
     * @param {Array} trades - Array of trade objects
     */
    updateTrades(trades) {
        this.trades = trades || [];
        this.renderTable();
        this.updateTradeCount();
    }

    /**
     * Update filtered trades and re-render with loading state
     * @param {Array} filteredTrades - Array of filtered trade objects
     */
    updateFilteredTrades(filteredTrades) {
        this.filteredTrades = filteredTrades || [];
        
        // Show loading message for larger datasets
        if (this.filteredTrades.length > 20) {
            this.showLoadingMessage('Rendering trades...');
        }
        
        this.renderTable();
        this.updateTradeCount();
        
        // Hide loading message after render
        setTimeout(() => {
            this.hideLoadingMessage();
        }, 300);
    }
    
    /**
     * Show loading message
     */
    showLoadingMessage(message) {
        let loadingMsg = document.querySelector('.table-loading-message');
        if (!loadingMsg) {
            loadingMsg = document.createElement('div');
            loadingMsg.className = 'loading-message table-loading-message';
            this.bodyElement.parentElement.appendChild(loadingMsg);
        }
        loadingMsg.textContent = message;
        loadingMsg.style.opacity = '1';
    }
    
    /**
     * Hide loading message
     */
    hideLoadingMessage() {
        const loadingMsg = document.querySelector('.table-loading-message');
        if (loadingMsg) {
            loadingMsg.classList.add('fade-out');
            setTimeout(() => {
                if (loadingMsg.parentElement) {
                    loadingMsg.parentElement.removeChild(loadingMsg);
                }
            }, 300);
        }
    }

    /**
     * Render table body with current trades and smooth animations
     */
    renderTable() {
        if (!this.bodyElement) return;
        
        // Clear any pending render
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
            this.renderTimeout = null;
        }
        
        // Debounce rapid render calls
        this.renderTimeout = setTimeout(() => {
            this.performRender();
            this.renderTimeout = null;
        }, 50);
    }
    
    /**
     * Perform the actual render operation with differential updates
     */
    performRender() {
        if (!this.bodyElement) return;
        
        if (this.filteredTrades.length === 0) {
            this.renderEmptyState();
            return;
        }

        // Use differential update instead of complete rebuild
        this.performDifferentialUpdate();
    }
    
    /**
     * Perform differential update - only modify changed rows
     */
    performDifferentialUpdate() {
        // Get existing rows and their trade IDs
        const existingRows = Array.from(this.bodyElement.children);
        const existingTradeIds = existingRows.map(row => row.dataset.tradeId).filter(id => id);
        
        // Generate trade IDs for new filtered trades
        const newTradeIds = this.filteredTrades.map(trade => 
            this.generateTradeId(trade));
        
        // Remove rows that are no longer in filtered data
        existingRows.forEach(row => {
            const tradeId = row.dataset.tradeId;
            if (tradeId && !newTradeIds.includes(tradeId)) {
                row.remove();
            }
        });
        
        // Add new rows that don't exist yet
        this.filteredTrades.forEach((trade, index) => {
            const tradeId = this.generateTradeId(trade);
            
            // Check if row already exists
            const existingRow = this.bodyElement.querySelector(`[data-trade-id="${tradeId}"]`);
            if (!existingRow) {
                const row = this.createTradeRow(trade);
                row.dataset.tradeId = tradeId;
                
                // Insert at correct position to maintain order
                const nextSibling = this.bodyElement.children[index];
                if (nextSibling) {
                    this.bodyElement.insertBefore(row, nextSibling);
                } else {
                    this.bodyElement.appendChild(row);
                }
            }
        });
        
        // Reorder existing rows if needed (for sorting)
        this.reorderExistingRows();
    }
    
    /**
     * Generate unique trade ID for differential updates
     * @param {Object} trade - Trade object
     * @returns {string} - Unique trade ID
     */
    generateTradeId(trade) {
        return trade.id || `${trade.asset}_${trade.direction}_${trade.entry_price}_${trade.file_timestamp}`;
    }
    
    /**
     * Reorder existing rows to match current sort order
     */
    reorderExistingRows() {
        const currentRows = Array.from(this.bodyElement.children);
        const sortedTradeIds = this.filteredTrades.map(trade => this.generateTradeId(trade));
        
        // Only reorder if the current order doesn't match expected order
        const currentOrder = currentRows.map(row => row.dataset.tradeId);
        const needsReordering = !sortedTradeIds.every((id, index) => id === currentOrder[index]);
        
        if (needsReordering) {
            // Create document fragment for efficient reordering
            const fragment = document.createDocumentFragment();
            
            sortedTradeIds.forEach(tradeId => {
                const row = this.bodyElement.querySelector(`[data-trade-id="${tradeId}"]`);
                if (row) {
                    fragment.appendChild(row);
                }
            });
            
            this.bodyElement.appendChild(fragment);
        }
    }
    
    /**
     * Render skeleton loading rows
     */
    renderSkeletonRows() {
        const fragment = document.createDocumentFragment();
        const rowCount = Math.min(this.filteredTrades.length, 5); // Show max 5 skeleton rows
        
        for (let i = 0; i < rowCount; i++) {
            const skeletonRow = this.createSkeletonRow();
            fragment.appendChild(skeletonRow);
        }
        
        this.bodyElement.appendChild(fragment);
    }
    
    /**
     * Create skeleton row for loading state
     */
    createSkeletonRow() {
        const row = document.createElement('tr');
        row.className = 'skeleton-table-row';
        
        const cells = ['asset', 'direction', 'price', 'price', 'price', 'confidence', 'risk-reward', 'time', 'actions'];
        cells.forEach(cellType => {
            const cell = document.createElement('td');
            const skeleton = document.createElement('div');
            skeleton.className = `skeleton skeleton-cell ${cellType}`;
            cell.appendChild(skeleton);
            row.appendChild(cell);
        });
        
        return row;
    }
    
    /**
     * Render trades with staggered animation
     */
    renderTradesWithAnimation() {
        // Clear skeleton rows
        while (this.bodyElement.firstChild) {
            this.bodyElement.removeChild(this.bodyElement.firstChild);
        }
        
        // Render trades with staggered animation
        const fragment = document.createDocumentFragment();
        this.filteredTrades.forEach((trade, index) => {
            const row = this.createTradeRow(trade);
            row.classList.add('table-row-enter');
            row.style.animationDelay = `${index * 50}ms`;
            fragment.appendChild(row);
        });
        
        this.bodyElement.appendChild(fragment);
    }

    /**
     * Create table row for a trade
     * @param {Object} trade - Trade object
     * @returns {HTMLElement} - Table row element
     */
    createTradeRow(trade) {
        const row = document.createElement('tr');
        row.className = 'table-row';
        // Use consistent ID generation for differential updates
        row.dataset.tradeId = this.generateTradeId(trade);
        
        // Single click handler for trade details panel
        row.addEventListener('click', () => {
            if (window.tradeDetailsPanel) {
                // console.log('Opening trade details for:', trade.id);
                window.tradeDetailsPanel.showTradeDetails(trade);
            }
        });

        // Asset
        const assetCell = document.createElement('td');
        assetCell.className = 'asset-cell';
        
        // Create asset name
        const assetName = document.createElement('strong');
        assetName.textContent = trade.asset || '-';
        assetCell.appendChild(assetName);
        
        // Add signal type badge
        if (trade.signal_type) {
            const signalBadge = document.createElement('span');
            signalBadge.className = 'signal-type-badge';
            
            // Map signal_type to display text
            const signalTypeMap = {
                'standard': 'Liquidity',
                'pure_liquidity': 'Liquidity',
                'ta_based': 'Enhanced',
                'enhanced': 'Enhanced',
                'ta_range': 'Ranging',
                'v3': 'V3'
            };
            
            signalBadge.textContent = signalTypeMap[trade.signal_type] || trade.signal_type;
            assetCell.appendChild(signalBadge);
        }
        
        row.appendChild(assetCell);

        // Direction
        const directionCell = document.createElement('td');
        const directionBadge = document.createElement('span');
        directionBadge.className = `badge ${window.Helpers && window.Helpers.getDirectionClass ? window.Helpers.getDirectionClass(trade.direction) : 'badge-secondary'}`;
        directionBadge.textContent = (trade.direction || '').toUpperCase();
        directionCell.appendChild(directionBadge);
        row.appendChild(directionCell);

        // Entry Price with live price badge
        const entryCell = document.createElement('td');
        entryCell.className = 'price-cell';
        entryCell.textContent = '$' + Helpers.formatCurrency(trade.entry_price, 4);
        
        // Add current price badge next to entry price
        if (window.BrowserPriceService) {
            const currentPrice = window.BrowserPriceService.getPrice(trade.asset);
            if (currentPrice) {
                const priceBadge = document.createElement('span');
                priceBadge.className = 'price-badge';
                priceBadge.textContent = ' $' + Helpers.formatCurrency(currentPrice, 4);
                entryCell.appendChild(priceBadge);
            }
        }
        
        row.appendChild(entryCell);

        // Target Price
        const targetCell = document.createElement('td');
        targetCell.className = 'price-cell';
        targetCell.textContent = '$' + Helpers.formatCurrency(trade.target_price, 4);
        row.appendChild(targetCell);

        // Stop Price
        const stopCell = document.createElement('td');
        stopCell.className = 'price-cell';
        stopCell.textContent = '$' + Helpers.formatCurrency(trade.stop_price, 4);
        row.appendChild(stopCell);

        // Confidence
        const confidenceCell = document.createElement('td');
        confidenceCell.className = 'confidence-cell';
        const confidence = parseFloat(trade.confidence) || 0;
        
        const confidenceContainer = document.createElement('div');
        confidenceContainer.style.display = 'flex';
        confidenceContainer.style.alignItems = 'center';
        
        const confidenceText = document.createElement('span');
        confidenceText.textContent = Helpers.formatPercentage(confidence);
        
        const confidenceBar = document.createElement('div');
        confidenceBar.className = 'confidence-bar';
        const confidenceFill = document.createElement('div');
        confidenceFill.className = 'confidence-fill';
        confidenceFill.style.width = `${confidence * 100}%`;
        confidenceBar.appendChild(confidenceFill);
        
        confidenceContainer.appendChild(confidenceText);
        confidenceContainer.appendChild(confidenceBar);
        confidenceCell.appendChild(confidenceContainer);
        row.appendChild(confidenceCell);

        // Risk/Reward - Calculate from entry/TP/SL
        const rrCell = document.createElement('td');
        rrCell.className = 'risk-reward-cell';
        
        // Calculate R/R from entry prices, not current price
        const entry = parseFloat(trade.entry_price);
        const tp = parseFloat(trade.target_price);
        const sl = parseFloat(trade.stop_price);
        
        if (entry && tp && sl && !isNaN(entry) && !isNaN(tp) && !isNaN(sl)) {
            const reward = Math.abs(tp - entry);
            const risk = Math.abs(entry - sl);
            const rr = risk > 0 ? (reward / risk).toFixed(2) : '-';
            rrCell.textContent = rr;
        } else {
            rrCell.textContent = '-';
        }
        
        row.appendChild(rrCell);

        // Time/Date
        const dateCell = document.createElement('td');
        dateCell.className = 'date-cell';
        const timeDisplay = new Date(trade.file_timestamp || trade.created_at).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        dateCell.textContent = timeDisplay;
        dateCell.title = Helpers.formatDate(trade.file_timestamp || trade.created_at);
        row.appendChild(dateCell);

        // Actions cell with lightning button
        const actionsCell = document.createElement('td');
        actionsCell.className = 'actions-cell';
        
        const quickTradeBtn = document.createElement('button');
        quickTradeBtn.className = 'btn btn-xs btn-warning quick-trade-btn';
        quickTradeBtn.innerHTML = 'Quick';
        quickTradeBtn.title = 'Execute quick trade';
        
        // Add click handler for quick trade
        quickTradeBtn.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent row click
            this.executeQuickTrade(trade);
        });
        
        actionsCell.appendChild(quickTradeBtn);
        row.appendChild(actionsCell);

        return row;
    }



    /**
     * Get CSS class for trend direction badge
     * @param {string} bias - Dominant bias value
     * @returns {string} - CSS class name
     */
    getTrendClass(bias) {
        if (!bias) return 'badge-secondary';
        
        const normalizedBias = bias.toLowerCase();
        if (normalizedBias.includes('bull') || normalizedBias.includes('long')) {
            return 'badge-success';
        } else if (normalizedBias.includes('bear') || normalizedBias.includes('short')) {
            return 'badge-danger';
        } else {
            return 'badge-warning';
        }
    }

    /**
     * Get color for quality score bar
     * @param {number} score - Quality score (0-1)
     * @returns {string} - CSS color
     */
    getQualityColor(score) {
        if (score >= 0.7) return '#28a745'; // Green for high quality
        if (score >= 0.4) return '#ffc107'; // Yellow for medium quality
        return '#dc3545'; // Red for low quality
    }

    /**
     * Create support/resistance display element
     * @param {Object} trade - Trade object
     * @returns {HTMLElement} - Container element
     */
    createSupportResistanceDisplay(trade) {
        const container = document.createElement('div');
        container.className = 'support-resistance-container';
        container.style.fontSize = '0.8em';
        
        // Parse support and resistance levels from JSON strings
        let supportLevels = [];
        let resistanceLevels = [];
        
        try {
            if (trade.support_levels && typeof trade.support_levels === 'string') {
                supportLevels = JSON.parse(trade.support_levels);
            } else if (Array.isArray(trade.support_levels)) {
                supportLevels = trade.support_levels;
            }
            
            if (trade.resistance_levels && typeof trade.resistance_levels === 'string') {
                resistanceLevels = JSON.parse(trade.resistance_levels);
            } else if (Array.isArray(trade.resistance_levels)) {
                resistanceLevels = trade.resistance_levels;
            }
        } catch (e) {
            // Fallback to closest values if JSON parsing fails
            if (trade.closest_support) supportLevels = [trade.closest_support];
            if (trade.closest_resistance) resistanceLevels = [trade.closest_resistance];
        }
        
        // Display support levels
        if (supportLevels.length > 0) {
            const supportDiv = document.createElement('div');
            supportDiv.style.color = '#28a745';
            supportDiv.style.marginBottom = '2px';
            supportDiv.innerHTML = `S: ${supportLevels.slice(0, 2).map(level => Helpers.formatCurrency(level, 4)).join(', ')}`;
            container.appendChild(supportDiv);
        }
        
        // Display resistance levels
        if (resistanceLevels.length > 0) {
            const resistanceDiv = document.createElement('div');
            resistanceDiv.style.color = '#dc3545';
            resistanceDiv.innerHTML = `R: ${resistanceLevels.slice(0, 2).map(level => Helpers.formatCurrency(level, 4)).join(', ')}`;
            container.appendChild(resistanceDiv);
        }
        
        // Fallback if no levels available
        if (supportLevels.length === 0 && resistanceLevels.length === 0) {
            container.textContent = 'N/A';
            container.style.color = '#7d8590';
        }
        
        return container;
    }

    /**
     * Render empty state when no trades
     */
    renderEmptyState() {
        // Clear existing rows first for differential update consistency
        while (this.bodyElement.firstChild) {
            this.bodyElement.removeChild(this.bodyElement.firstChild);
        }
        
        const row = document.createElement('tr');
        const cell = document.createElement('td');
        cell.colSpan = 10;
        cell.style.textAlign = 'center';
        cell.style.padding = '2rem';
        cell.style.color = '#7d8590';
        cell.textContent = 'No trades found matching current filters';
        row.appendChild(cell);
        this.bodyElement.appendChild(row);
    }

    /**
     * Handle column sort
     * @param {string} column - Column to sort by
     */
    handleSort(column) {
        if (this.sortColumn === column) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortOrder = 'desc';
        }

        this.updateSortIndicators();
        this.sortTrades();
        this.renderTable();
    }

    /**
     * Update sort indicators in header
     */
    updateSortIndicators() {
        const headers = this.headerElement.querySelectorAll('th');
        headers.forEach(th => {
            th.classList.remove('sort-asc', 'sort-desc');
            
            if (th.dataset.column === this.sortColumn) {
                th.classList.add(this.sortOrder === 'asc' ? 'sort-asc' : 'sort-desc');
            }
        });
    }

    /**
     * Sort current filtered trades
     */
    sortTrades() {
        this.filteredTrades.sort((a, b) => {
            let valueA = a[this.sortColumn];
            let valueB = b[this.sortColumn];

            // Handle different data types
            if (this.sortColumn === 'file_timestamp' || this.sortColumn === 'created_at') {
                valueA = new Date(valueA || 0).getTime();
                valueB = new Date(valueB || 0).getTime();
            } else if (typeof valueA === 'string') {
                valueA = valueA.toLowerCase();
                valueB = valueB.toLowerCase();
            } else {
                valueA = parseFloat(valueA) || 0;
                valueB = parseFloat(valueB) || 0;
            }

            let comparison = 0;
            if (valueA > valueB) comparison = 1;
            if (valueA < valueB) comparison = -1;

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    /**
     * Handle row click event
     * @param {Object} trade - Trade object
     */
    handleRowClick(trade) {
        if (this.onRowClick) {
            this.onRowClick(trade);
        }
    }

    /**
     * Set row click callback
     * @param {Function} callback - Callback function
     */
    onRowClicked(callback) {
        this.onRowClick = callback;
    }

    /**
     * Update trade count display
     */
    updateTradeCount() {
        const countElement = document.getElementById('trade-count');
        if (!countElement) return;

        const filtered = this.filteredTrades.length;
        countElement.textContent = `${filtered} trades`;
    }

    /**
     * Export trades to CSV
     */
    exportTrades() {
        if (this.filteredTrades.length === 0) {
            Helpers.showError('No trades to export');
            return;
        }

        const headers = [
            'asset', 'direction', 'entry_price', 'target_price', 'stop_price',
            'confidence', 'risk_reward', 'cluster_count', 'created_at', 'status'
        ];

        const csvContent = Helpers.generateCSV(this.filteredTrades, headers);
        const filename = `evplus_trades_${new Date().toISOString().split('T')[0]}.csv`;
        
        Helpers.downloadCSV(csvContent, filename);
    }

    /**
     * Get current sort settings
     * @returns {Object} - Sort settings
     */
    getSortSettings() {
        return {
            column: this.sortColumn,
            order: this.sortOrder
        };
    }

    /**
     * Set sort settings
     * @param {string} column - Column to sort by
     * @param {string} order - Sort order (asc/desc)
     */
    setSortSettings(column, order) {
        this.sortColumn = column;
        this.sortOrder = order;
        this.updateSortIndicators();
        this.sortTrades();
        this.renderTable();
    }

    /**
     * Refresh table display
     */
    refresh() {
        this.renderTable();
        this.updateTradeCount();
    }

    /**
     * Validate price before trade execution - warn if price moved against signal
     * @param {Object} trade - Trade object with entry_price, direction, asset
     * @returns {Promise<boolean>} - True if should proceed, false if cancelled
     */
    async validatePriceBeforeTrade(trade) {
        // Get current price from price service
        const currentPriceStr = window.BrowserPriceService?.getPrice(trade.asset);
        if (!currentPriceStr) {
            return true; // No price data, proceed without warning
        }
        
        const currentPrice = parseFloat(currentPriceStr);
        const entryPrice = parseFloat(trade.entry_price);
        const direction = (trade.direction || '').toLowerCase().trim();
        
        // Validate inputs
        if (!currentPrice || !entryPrice || isNaN(currentPrice) || isNaN(entryPrice) || !direction) {
            return true; // Invalid data, proceed
        }
        
        // Validate direction
        if (direction !== 'long' && direction !== 'short') {
            return true; // Unknown direction, proceed
        }
        
        // Staleness check: Warn if price moved away from signal
        const isLong = direction === 'long';
        const priceMovedAgainst = isLong 
            ? currentPrice < entryPrice  // LONG: current < entry = WARN
            : entryPrice < currentPrice; // SHORT: entry < current = WARN
        
        if (!priceMovedAgainst) {
            return true; // Price is favorable, proceed
        }
        
        // Calculate percentage difference
        const priceDiff = ((Math.abs(currentPrice - entryPrice) / entryPrice) * 100).toFixed(2);
        const priceDirection = isLong ? 'up' : 'down';
        
        // Show warning modal
        return new Promise((resolve) => {
            const modalHtml = `
                <div class="modal-header">
                    <h2>⚠️ Price Movement Alert</h2>
                    <button class="modal-close" id="modal-close-btn">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="price-info-grid">
                        <div class="price-grid">
                            <div class="price-label">Asset:</div>
                            <div class="price-value">${trade.asset}</div>
                            <div class="price-label">Direction:</div>
                            <div class="price-value">${direction.toUpperCase()}</div>
                            <div class="price-label">Signal Entry:</div>
                            <div class="price-value">$${entryPrice.toFixed(4)}</div>
                            <div class="price-label">Current Price:</div>
                            <div class="price-value current-price">$${currentPrice.toFixed(4)}</div>
                        </div>
                    </div>
                    <div class="warning-box">
                        <p class="warning-title">⚠️ WARNING: THIS POSITION WILL EXECUTE AT CURRENT PRICE</p>
                        <p class="warning-subtitle">May hit stop loss immediately</p>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn-cancel" id="cancel-trade-btn">Cancel</button>
                    <button class="btn-continue" id="continue-trade-btn">Continue Trade</button>
                </div>
            `;
            
            // Create modal overlay
            const overlay = document.createElement('div');
            overlay.id = 'price-validation-modal';
            overlay.className = 'modal-overlay';
            
            const modal = document.createElement('div');
            modal.className = 'modal-container';
            modal.innerHTML = modalHtml;
            
            overlay.appendChild(modal);
            document.body.appendChild(overlay);
            
            // Button handlers
            const continueBtn = modal.querySelector('#continue-trade-btn');
            const cancelBtn = modal.querySelector('#cancel-trade-btn');
            const closeBtn = modal.querySelector('#modal-close-btn');
            
            const cleanup = () => {
                document.body.removeChild(overlay);
            };
            
            continueBtn.addEventListener('click', () => {
                cleanup();
                resolve(true);
            });
            
            cancelBtn.addEventListener('click', () => {
                cleanup();
                resolve(false);
            });
            
            closeBtn.addEventListener('click', () => {
                cleanup();
                resolve(false);
            });
            
            // Close on overlay click
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    cleanup();
                    resolve(false);
                }
            });
            
            // Close on Escape
            const escHandler = (e) => {
                if (e.key === 'Escape') {
                    cleanup();
                    document.removeEventListener('keydown', escHandler);
                    resolve(false);
                }
            };
            document.addEventListener('keydown', escHandler);
        });
    }

    /**
     * Execute quick trade for historical trades
     * @param {Object} trade - Trade object
     */
    async executeQuickTrade(trade) {
        try {
            // console.log('Executing quick trade for:', trade);
            
            // Validate price before proceeding
            const shouldProceed = await this.validatePriceBeforeTrade(trade);
            if (!shouldProceed) {
                console.log('Quick trade cancelled by user due to price movement');
                return;
            }
            
            // Check if order client system is available
            if (!window.orderClient || !window.orderClient.isInitialized) {
                throw new Error('Order client system not available');
            }

            // === CALCULATE POSITION SIZE USING LEVERAGE-AWARE METHOD ===
            try {
                // 1. Get position size from current view's input (or fallback to AT bot)
                const posSettings = await this.getQuickTradePositionSize();
                
                // Parse position size (supports % and $)
                const parsed = window.PositionSizeParser.parsePositionSize(posSettings.positionSize);
                const scaleUpEnabled = posSettings.scaleUpEnabled;
                
                if (parsed && parsed.value > 0) {
                    // 2. Get account value
                    let accountValue = await this.getAccountValue();
                    const entryPrice = parseFloat(trade.entry_price);
                    
                    // 3. Validate inputs
                    if (!entryPrice || entryPrice <= 0 || isNaN(entryPrice)) {
                        console.error(`🛒 ❌ Invalid entry price for ${trade.asset}: ${trade.entry_price}`);
                        trade.size = 0.001;
                    } else if (accountValue > 0) {
                        console.log('🔍 DEBUG: ✅ Validation passed - using leverage-aware calculation');
                        
                        // STEP 1: Calculate MARGIN amount (what user is risking)
                        let marginAmount;
                        
                        if (parsed.type === 'percentage') {
                            // EXISTING PATH: % of account
                            marginAmount = (accountValue * parsed.value) / 100;
                            console.log(`🛒 💵 STEP 1 - Margin (Percentage): $${accountValue.toFixed(2)} × ${parsed.value}% = $${marginAmount.toFixed(2)}`);
                            
                        } else if (parsed.type === 'usd') {
                            // NEW PATH: Fixed USD margin
                            marginAmount = parsed.value;
                            console.log(`🛒 💵 STEP 1 - Margin (Fixed USD): $${marginAmount.toFixed(2)}`);
                            
                            // Validation: Don't exceed account balance
                            if (marginAmount > accountValue) {
                                const originalMargin = marginAmount;
                                marginAmount = accountValue * 0.95; // Cap at 95%
                                console.warn(`🛒 ⚠️ Margin $${originalMargin.toFixed(2)} exceeds account, capped to $${marginAmount.toFixed(2)}`);
                            }
                            
                            const percentOfAccount = (marginAmount / accountValue) * 100;
                            console.log(`🛒 💵          This is ${percentOfAccount.toFixed(2)}% of your account`);
                        }
                        
                        // STEP 2: Get asset's max leverage
                        let maxLeverage = 20; // Default
                        
                        // Try TradeDataManager first (fastest, globally cached)
                        if (window.TradeDataManager) {
                            try {
                                const assets = window.TradeDataManager.getAssets();
                                const assetData = assets.find(a => a.name === trade.asset);
                                if (assetData?.maxLeverage) {
                                    maxLeverage = assetData.maxLeverage;
                                    console.log(`🛒 📈 STEP 2 - Leverage (TradeDataManager): ${maxLeverage}x for ${trade.asset}`);
                                }
                            } catch (e) {
                                console.warn('🛒 ⚠️ TradeDataManager lookup failed:', e);
                            }
                        }
                        
                        // Fallback: Fetch from Hyperliquid API if not found
                        if ((!maxLeverage || maxLeverage === 20) && window.BrowserHyperliquidClient) {
                            try {
                                console.log(`🛒 🔄 Fetching ${trade.asset} leverage from Hyperliquid API...`);
                                const assetData = await window.BrowserHyperliquidClient.getAssetMetadata();
                                const asset = assetData?.universe?.find(a => a.name === trade.asset);
                                if (asset?.maxLeverage) {
                                    maxLeverage = asset.maxLeverage;
                                    console.log(`🛒 ✅ Found ${trade.asset} leverage from API: ${maxLeverage}x`);
                                }
                            } catch (e) {
                                console.warn('🛒 ⚠️ API leverage lookup failed:', e);
                            }
                        }
                        
                        // Validate leverage minimum
                        if (!maxLeverage || maxLeverage < 3) {
                            console.warn(`🛒 ⚠️ Invalid leverage ${maxLeverage}x, using default 20x`);
                            maxLeverage = 20;
                        }
                        
                        console.log(`🛒 📊 STEP 2 - Final Leverage: ${maxLeverage}x for ${trade.asset}`);
                        
                        // STEP 3: Calculate NOTIONAL value
                        // BOTH % and $ modes: marginAmount = margin to risk (before leverage)
                        let notionalAmount = marginAmount * maxLeverage;
                        let finalMargin = marginAmount;
                        
                        if (parsed.type === 'percentage') {
                            console.log(`🛒 💎 STEP 3 - Notional (% mode): $${marginAmount.toFixed(2)} margin × ${maxLeverage}x = $${notionalAmount.toFixed(2)} notional`);
                        } else {
                            console.log(`🛒 💎 STEP 3 - Notional ($ mode): $${marginAmount.toFixed(2)} margin × ${maxLeverage}x = $${notionalAmount.toFixed(2)} notional`);
                        }
                        
                        // STEP 4: Scale up check (ensure notional meets Hyperliquid's $10 minimum)
                        if (scaleUpEnabled && notionalAmount < 10.00) {
                            const originalNotional = notionalAmount;
                            const originalMargin = finalMargin;
                            notionalAmount = 13.00; // $13 buffer for safety
                            finalMargin = notionalAmount / maxLeverage;
                            console.log(`🛒 📏 STEP 4 - Scaled up: $${originalNotional.toFixed(2)} → $${notionalAmount.toFixed(2)} notional`);
                            console.log(`🛒 📏          Margin: $${originalMargin.toFixed(2)} → $${finalMargin.toFixed(2)} (${(finalMargin/accountValue*100).toFixed(2)}% of account)`);
                        } else {
                            console.log(`🛒 ✅ STEP 4 - No scaling needed (notional $${notionalAmount.toFixed(2)} >= $10)`);
                        }
                        
                        // STEP 5: Convert notional to asset quantity
                        const assetSize = notionalAmount / entryPrice;
                        trade.size = parseFloat(assetSize.toPrecision(6));
                        
                        console.log(`🛒 📊 STEP 5 - Final Size: $${notionalAmount.toFixed(2)} / $${entryPrice} = ${trade.size} ${trade.asset}`);
                        console.log(`🛒 ✅ Quick Trade Position Summary:`);
                        if (parsed.type === 'percentage') {
                            console.log(`   Mode: ${parsed.type} (${parsed.value}% of account)`);
                        } else {
                            console.log(`   Mode: ${parsed.type} ($${parsed.value} margin)`);
                        }
                        console.log(`   Account Value: $${accountValue.toFixed(2)}`);
                        console.log(`   Margin Used: $${finalMargin.toFixed(2)} (${(finalMargin/accountValue*100).toFixed(2)}%)`);
                        console.log(`   Leverage: ${maxLeverage}x`);
                        console.log(`   Notional Value: $${notionalAmount.toFixed(2)}`);
                        console.log(`   Entry Price: $${entryPrice}`);
                        console.log(`   Asset Size: ${trade.size} ${trade.asset}`);
                    } else {
                        console.log('🛒 ❌ Validation failed - using fallback');
                        trade.size = trade.size || 0.001;
                    }
                } else {
                    console.log('🛒 ❌ Auto trade status fetch failed');
                    trade.size = trade.size || 0.001;
                }
            } catch (error) {
                console.error('🛒 Quick Buy: Error calculating position size:', error);
                console.log('🔍 DEBUG: Exception details:', error.stack);
                trade.size = trade.size || 0.001;
            }
            
            console.log('🔍 DEBUG: Final trade object before sending to order client:', JSON.stringify(trade, null, 2));
            console.log('🔍 DEBUG: Final trade.size value:', trade.size, 'Type:', typeof trade.size);
            // === END POSITION SIZE CALCULATION ===

            // Execute the order
            const result = await window.orderClient.placeBuyOrder(trade);
            
            if (result.success) {
                // console.log('Quick trade executed successfully:', result);
                
                // Show success notification
                if (window.NotificationManager) {
                    window.NotificationManager.showNotification({
                        type: 'success',
                        title: 'Order Placed Successfully',
                        message: `${result.orderCount} orders placed with CLOID: ${result.cloid}`,
                        duration: 5000
                    });
                }
            } else {
                console.error('Quick trade failed:', result.error);
                
                // Show error notification
                if (window.NotificationManager) {
                    window.NotificationManager.showNotification({
                        type: 'error',
                        title: 'Order Failed',
                        message: result.error,
                        duration: 5000
                    });
                }
            }
            
            return result;
            
        } catch (error) {
            console.error('🛒 Quick Buy: Error executing quick trade:', error);
            console.log('🔍 DEBUG: Exception details:', error.stack);
            
            // Show error notification
            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'error',
                    title: 'Trade Execution Failed',
                    message: error.message,
                    duration: 5000
                });
            }
            
            return { success: false, error: error.message };
        }
    }

    /**
     * Get account value with multiple fallbacks (similar to auto trade bot)
     */
    async getAccountValue() {
        try {
            console.log('🔍 DEBUG: [getAccountValue] Starting account value fetch...');
            
            // Method 1: Check window.lastPositionsData (from terminal view)
            console.log('🔍 DEBUG: [getAccountValue] Checking window.lastPositionsData...');
            console.log('🔍 DEBUG: [getAccountValue] window.lastPositionsData exists:', !!window.lastPositionsData);
            
            if (window.lastPositionsData && window.lastPositionsData.accountSummary) {
                const accountValue = window.lastPositionsData.accountSummary.accountValue || 0;
                console.log('🔍 DEBUG: [getAccountValue] Account value from lastPositionsData:', accountValue);
                if (accountValue > 0) {
                    console.log('🔍 DEBUG: [getAccountValue] ✅ Got account value from positions data:', accountValue);
                    return accountValue;
                }
            } else {
                console.log('🔍 DEBUG: [getAccountValue] No account data in window.lastPositionsData');
            }
            
            // Method 2: Direct service call (fallback)
            console.log('🔍 DEBUG: [getAccountValue] Trying direct service call...');
            
            let userStateResult;
            if (window.BrowserHyperliquidClient && window.BrowserHyperliquidClient.isInitialized) {
                console.log('🔍 DEBUG: [getAccountValue] Using BrowserHyperliquidClient.getUserState');
                const userState = await window.BrowserHyperliquidClient.getUserState();
                userStateResult = { success: true, userState: userState };
            } else {
                console.log('🔍 DEBUG: [getAccountValue] Using IPC getUserState');
                userStateResult = await window.electronAPI.invoke('hyperliquid:getUserState');
            }
            console.log('🔍 DEBUG: [getAccountValue] getUserState result:', JSON.stringify(userStateResult, null, 2));
            
            if (userStateResult.success && userStateResult.userState && userStateResult.userState.marginSummary) {
                const accountValue = parseFloat(userStateResult.userState.marginSummary.accountValue) || 0;
                console.log('🔍 DEBUG: [getAccountValue] Account value from hyperliquid service:', accountValue);
                if (accountValue > 0) {
                    console.log('🔍 DEBUG: [getAccountValue] ✅ Got account value from hyperliquid service:', accountValue);
                    return accountValue;
                }
            } else {
                console.log('🔍 DEBUG: [getAccountValue] No valid account data from hyperliquid service');
            }
            
            console.log('🔍 DEBUG: [getAccountValue] ❌ No account value available from any source');
            return 0;
            
        } catch (error) {
            console.error('🔍 DEBUG: [getAccountValue] Error getting account value:', error);
            return 0;
        }
    }

    /**
     * Get quick trade position size from current view's input (or fallback to Auto Trade Bot)
     * @returns {Promise<Object>} { positionSize: string, scaleUpEnabled: boolean }
     */
    async getQuickTradePositionSize() {
        // Try Trades View-specific input first
        const sizeInput = document.getElementById('trades-position-size');
        const scaleUpCheckbox = document.getElementById('trades-scale-up-size');
        
        if (sizeInput && sizeInput.value) {
            return {
                positionSize: sizeInput.value.trim(),
                scaleUpEnabled: scaleUpCheckbox ? scaleUpCheckbox.checked : false
            };
        }
        
        // Fallback: Auto Trade Bot settings
        try {
            const autoTradeStatus = await window.electronAPI.invoke('autoTrade:getStatus');
            if (autoTradeStatus.success) {
                const currentMode = autoTradeStatus.status.mode;
                const modeSettings = currentMode === 'easy' 
                    ? autoTradeStatus.status.easyModeSettings 
                    : autoTradeStatus.status.hardModeSettings;
                
                return {
                    positionSize: modeSettings.positionSize,
                    scaleUpEnabled: modeSettings.scaleUpSize !== false
                };
            }
        } catch (error) {
            console.error('Failed to get position size settings:', error);
        }
        
        // Ultimate fallback
        return {
            positionSize: '2.5%',
            scaleUpEnabled: true // Always scale up by default
        };
    }

    /**
     * Clear table
     */
    clear() {
        this.trades = [];
        this.filteredTrades = [];
        this.renderTable();
        this.updateTradeCount();
    }
}

// Export for ES6 modules
export { TableComponent };

// Export singleton instance
window.TableComponent = new TableComponent();
